import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Table,
  Text,
  notify,
} from "components/ui";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdDelete, MdSearch, MdUploadFile } from "react-icons/md";
import {
  CustomerCompanyKbDocument,
  CustomerCompanyKbDocumentStatus,
  CustomerCompanyKbTestRetrievalResponse,
  customerCompanyAdminService,
} from "services/customerCompanyAdminService";
import { getThemeColor } from "theme/colors";
import { formatDate, formatFileSize } from "./format";

interface KnowledgeBaseTabProps {
  customerCompanyId: string;
}

const STATUS_COLORS: Record<CustomerCompanyKbDocumentStatus, string> = {
  Pending: "gray",
  Processing: "blue",
  Indexed: "green",
  Failed: "red",
  Deleted: "gray",
};

/**
 * Documents this company's assistant can retrieve from. Deliberately a flat list: the
 * CustomerCompany scope has no folders, no global flag and no web sources — those belong to the
 * tenant-wide knowledge base at /admin/knowledge-base.
 */
const KnowledgeBaseTab = ({ customerCompanyId }: KnowledgeBaseTabProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<CustomerCompanyKbDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<CustomerCompanyKbDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [query, setQuery] = useState("");
  const [testing, setTesting] = useState(false);
  const [retrieval, setRetrieval] =
    useState<CustomerCompanyKbTestRetrievalResponse | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setDocuments(
        await customerCompanyAdminService.listKbDocuments(customerCompanyId),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.kb.loadFailed", {
            defaultValue: "Failed to load knowledge base documents.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  /** Three-step upload: mint a presigned URL, PUT the bytes to S3, then confirm. */
  const handleFileSelected = async (file: File) => {
    setUploading(true);
    try {
      const contentType = file.type || "application/octet-stream";
      const upload = await customerCompanyAdminService.getKbUploadUrl(
        customerCompanyId,
        { fileName: file.name, contentType },
      );

      const s3Response = await fetch(upload.presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
      });
      if (!s3Response.ok) {
        throw new Error(`Upload failed with status ${s3Response.status}`);
      }

      const created = await customerCompanyAdminService.confirmKbUpload(
        customerCompanyId,
        {
          docId: upload.docId,
          fileName: file.name,
          contentType,
          s3Key: upload.s3Key,
        },
      );
      setDocuments((prev) => [created, ...prev]);
      notify({
        type: "success",
        title: t("app.customerCompanies.kb.uploaded", {
          defaultValue: "Document uploaded. Indexing starts shortly.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deleteKbDocument(
        customerCompanyId,
        deleteTarget.id,
      );
      setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      notify({
        type: "success",
        title: t("app.customerCompanies.kb.deleted", {
          defaultValue: "Document deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleTestRetrieval = async () => {
    if (!query.trim()) return;
    setTesting(true);
    try {
      setRetrieval(
        await customerCompanyAdminService.testKbRetrieval(
          customerCompanyId,
          query.trim(),
        ),
      );
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setTesting(false);
    }
  };

  const cardProps = {
    borderWidth: "1px",
    borderColor: getThemeColor("card.border"),
    borderRadius: "md",
  } as const;

  return (
    <Stack gap={4}>
      <Card.Root {...cardProps}>
        <Card.Body>
          <Stack gap={3}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Stack gap={1}>
                <Heading size="sm">
                  {t("app.customerCompanies.kb.title", {
                    defaultValue: "Knowledge base",
                  })}
                </Heading>
                <Text fontSize="sm" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.kb.subtitle", {
                    defaultValue:
                      "Documents the assistant can draw on for this company only.",
                  })}
                </Text>
              </Stack>
              <HStack gap={2}>
                <RefreshButton onClick={load} loading={loading} />
                <Button
                  intent="primary"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <MdUploadFile />{" "}
                  {uploading
                    ? t("app.customerCompanies.kb.uploading", {
                        defaultValue: "Uploading...",
                      })
                    : t("app.customerCompanies.kb.upload", {
                        defaultValue: "Upload document",
                      })}
                </Button>
              </HStack>
            </Flex>

            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFileSelected(file);
              }}
            />

            {error && (
              <InfoBox variant="error">
                <Text color="red.700">{error}</Text>
              </InfoBox>
            )}

            {loading ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.kb.loading", {
                  defaultValue: "Loading documents…",
                })}
              </Text>
            ) : documents.length === 0 ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.kb.empty", {
                  defaultValue: "No documents uploaded for this company yet.",
                })}
              </Text>
            ) : (
              <Box overflowX="auto">
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.kb.columns.fileName", {
                          defaultValue: "File",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.kb.columns.status", {
                          defaultValue: "Status",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.kb.columns.size", {
                          defaultValue: "Size",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.customerCompanies.kb.columns.uploaded", {
                          defaultValue: "Uploaded",
                        })}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.common.actions", { defaultValue: "Actions" })}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {documents.map((doc) => (
                      <Table.Row key={doc.id}>
                        <Table.Cell>
                          <Stack gap={0}>
                            <Text fontSize="sm">{doc.fileName}</Text>
                            {doc.errorMessage && (
                              <Text fontSize="xs" color="red.600">
                                {doc.errorMessage}
                              </Text>
                            )}
                          </Stack>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge
                            colorPalette={STATUS_COLORS[doc.status] ?? "gray"}
                            variant="subtle"
                          >
                            {doc.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {formatFileSize(doc.fileSizeBytes)}
                        </Table.Cell>
                        <Table.Cell>{formatDate(doc.createdAt)}</Table.Cell>
                        <Table.Cell>
                          <Button
                            intent="ghost"
                            onClick={() => setDeleteTarget(doc)}
                          >
                            <MdDelete />{" "}
                            {t("app.common.delete", { defaultValue: "Delete" })}
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      {/* Test retrieval — check what the assistant would actually find */}
      <Card.Root {...cardProps}>
        <Card.Body>
          <Stack gap={3}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.kb.testRetrieval", {
                  defaultValue: "Test retrieval",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.kb.testRetrievalHelp", {
                  defaultValue:
                    "Search only this company's documents, exactly as the assistant does at runtime.",
                })}
              </Text>
            </Stack>

            <HStack gap={2}>
              <Input
                placeholder={t("app.customerCompanies.kb.queryPlaceholder", {
                  defaultValue: "e.g. what is the refund window?",
                })}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleTestRetrieval()}
                flex={1}
              />
              <Button
                intent="primary"
                onClick={handleTestRetrieval}
                disabled={testing || !query.trim()}
              >
                <MdSearch />{" "}
                {testing
                  ? t("app.customerCompanies.kb.searching", {
                      defaultValue: "Searching...",
                    })
                  : t("app.common.search", { defaultValue: "Search" })}
              </Button>
            </HStack>

            {retrieval && !retrieval.success && (
              <InfoBox variant="error">
                <Text color="red.700">
                  {retrieval.errorMessage ??
                    t("app.errors.requestFailed", {
                      defaultValue: "Request failed",
                    })}
                </Text>
              </InfoBox>
            )}

            {retrieval?.success && retrieval.chunks.length === 0 && (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.kb.noMatches", {
                  defaultValue: "No matching content found.",
                })}
              </Text>
            )}

            {retrieval?.success && retrieval.chunks.length > 0 && (
              <Stack gap={2}>
                {retrieval.chunks.map((chunk, index) => (
                  <Stack
                    key={`${chunk.sourceFileName ?? "chunk"}-${index}`}
                    gap={1}
                    borderWidth="1px"
                    borderColor={getThemeColor("card.border")}
                    borderRadius="md"
                    p={3}
                  >
                    <HStack gap={2}>
                      <Badge variant="subtle">
                        {chunk.sourceFileName ??
                          t("app.customerCompanies.kb.unknownSource", {
                            defaultValue: "Unknown source",
                          })}
                      </Badge>
                      <Text fontSize="xs" color="var(--text-secondary-color)">
                        {chunk.score.toFixed(3)}
                      </Text>
                    </HStack>
                    <Text fontSize="sm">{chunk.content}</Text>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("app.customerCompanies.kb.deleteTitle", {
          defaultValue: "Delete document",
        })}
        message={t("app.customerCompanies.kb.deleteConfirm", {
          defaultValue:
            "This document will be removed from the company's knowledge base and the assistant will stop retrieving from it.",
        })}
      />
    </Stack>
  );
};

export default KnowledgeBaseTab;
